import styled from "@emotion/styled";
import { VisibilityOffOutlined, VisibilityOffRounded } from "@mui/icons-material";
import { Button, CircularProgress, FormControl, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, Alert as MuiAlert, TextField as MuiTextField, OutlinedInput, Typography } from "@mui/material";
import { spacing } from "@mui/system";
import { Formik } from "formik";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import AlertMessageComponent from "../../pages/components/AlertMessageComponent";
import { apiGatewayAxiosInstance } from "../../utils/axios";

const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)(spacing);

function FirstLogInResetPassword() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [errorResponse, setErrorResponse] = useState("");
    const [showNewPassword, setShowNewPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

    const handleClickShowNewPassword = () => {
        setShowNewPassword(show => !show);
    }

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(show => !show);
    }
    const { userId } = useParams();

    const handleMouseDownPassword = event => {
        event.preventDefault();
    };

    const handleResetPassword = async (password, errors) => {
        if (!password || errors) {
            setErrorResponse("Password is required");
            return;
        }
        try {
            setIsLoading(true);
            const response = await apiGatewayAxiosInstance.post(`/user_management/clients/set-password`, {
                password: password,
                orgUserId: userId
            });
            if (response.status === 200) {
                navigate("/auth/login")
            }
        } catch (error) {
            setIsLoading(false);

            let errorMessage = "Server not reachable.";

            if (error.response) {
                errorMessage = (error.response.data && error.response.data.message) ?? errorMessage;
            }
            setErrorResponse(errorMessage);
        }
    };

    return (
        <div>
            <Typography component='h1' variant='h4' align='center' gutterBottom>
                Reset Password
            </Typography>
            <Typography component='h2' variant='body1' align='center'>
                Enter your new password and confirm it to reset your password
            </Typography>
            <Formik
                initialValues={{
                    password: "",
                    confirmPassword: "",
                    submit: false,
                }}
                validationSchema={Yup.object().shape({
                    password: Yup.string().min(8, "Password must be at least 8 characters").required("Password is required"),
                    confirmPassword: Yup.string()
                        .oneOf([Yup.ref('password'), null], 'Passwords must match')
                        .required('Confirm password is required'),
                })}
                onSubmit={async (values, { errors, setErrors, setStatus, setSubmitting }) => {
                    try {
                        await handleResetPassword(values.password, errors);
                        setStatus({ success: true });
                    } catch (error) {
                        const message = error.message || "Something went wrong";
                        setStatus({ success: false });
                        setErrors({ submit: message });
                        setSubmitting(false);
                    }
                }}
            >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                    <form noValidate onSubmit={handleSubmit}>
                        {errors.submit && (
                            <Alert mt={2} mb={1} severity='warning'>
                                {errors.submit}
                            </Alert>
                        )}
                        <Grid alignItems={"center"} display={"flex"} flexDirection={"column"} gap={4} mt={4}>
                            <FormControl fullWidth variant="outlined"  error={Boolean(errors.password)}>
                                <InputLabel htmlFor="password">New Password</InputLabel>
                                <OutlinedInput
                                    type={showNewPassword ? "text" : "password"}
                                    name='password'
                                    label='New Password'
                                    value={values.password}
                                    error={Boolean(touched.password && errors.password)}
                                    fullWidth
                                    helperText={touched.password && errors.password}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    my={3}
                                    inputProps={{
                                        autocomplete: "new-password",
                                        form: {
                                            autocomplete: "off",
                                        },
                                    }}
                                    endAdornment={
                                        <InputAdornment position='end'>
                                            <IconButton
                                                aria-label='toggle password visibility'
                                                onClick={handleClickShowNewPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge='end'
                                            >
                                                {showNewPassword ? <VisibilityOffOutlined /> : <VisibilityOffRounded />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                <FormHelperText>{errors.password}</FormHelperText>

                            </FormControl>
                            <FormControl fullWidth variant="outlined"  error={Boolean(errors.confirmPassword)}>
                                <InputLabel htmlFor="password">Confirm Password</InputLabel>
                                <OutlinedInput
                                    type={showConfirmPassword ? "text" : "password"}
                                    name='confirmPassword'
                                    label='Confirm Password'
                                    value={values.confirmPassword}
                                    error={Boolean(errors.confirmPassword)}
                                    fullWidth
                                    helperText={ errors.confirmPassword}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    my={3}
                                    inputProps={{
                                        autocomplete: "new-password",
                                        form: {
                                            autocomplete: "off",
                                        },
                                    }}
                                    endAdornment={
                                        <InputAdornment position='end'>
                                            <IconButton
                                                aria-label='toggle password visibility'
                                                onClick={handleClickShowConfirmPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge='end'
                                            >
                                                {showConfirmPassword ? <VisibilityOffOutlined /> : <VisibilityOffRounded />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                <FormHelperText>{errors.confirmPassword}</FormHelperText>
                            </FormControl>

                            {isLoading ?
                                <Button variant='outlined'>
                                    <CircularProgress size={15} className='spinner' />
                                    resetting password
                                </Button>
                                :
                                <Button
                                    fullWidth
                                    variant='contained'
                                    color='primary'
                                    disabled={isSubmitting}
                                    type="submit"
                                >
                                    Reset Password
                                </Button>
                            }
                        </Grid>
                    </form>
                )}
            </Formik>
            <AlertMessageComponent
                message={errorResponse}
                fieldName={"getAllTemplatesByPageSizeErrorMessage"}
                handleClose={() => setErrorResponse("")}
                show={Boolean(errorResponse)}
            />
        </div>
    );
}

export default FirstLogInResetPassword;
